
import * as React from 'react';
import { downloadItemsActionControlFacet, IDownloaderProps } from '../../../../controls/downloadItemsActionControl';
import {
  withSelectedItems,
  withCurrentItemAsFallback,
  IDecorateWithSelectedItemsProps,
  IDecorateWithCurrentItemAsFallbackProps
} from '../../../decorators/selectors';
import { IWizardActionStateProps, HeadlessAction } from '../../../wizardAction/WizardAction';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';
import { ItemsScope } from '../../../itemsScope/ItemsScope';
import { getItemsScope } from '../../../decorators/connectToItemsScope';
import { connectToItemCache } from '../../../decorators/connectToItemCache';
import { IItemCache } from '../../../../models/IItemCacheObserver';
import { ISelectedActionProps, ActionSelector } from '../../../withAction/WithAction';

export interface IItemDownloadActionProps {
  itemKeys: string[];
  downloader: React.ComponentType<IDownloaderProps>;
}

export const ItemDownloadAction: React.ComponentType<IItemDownloadActionProps> = connectToItemCache((
  itemCache: IItemCache,
  props: IItemDownloadActionProps
): ISelectedActionProps => {
  const {
    itemKeys,
    downloader
  } = props;

  const [itemKey] = itemKeys;

  if (!itemKey) {
    return {
      action: null
    };
  }

  const { itemsScopeItemKey } = getItemsScope(itemCache);

  const controlHandler = itemCache.demandItemFacet(downloadItemsActionControlFacet, itemKey);

  const {
    downloader: Downloader,
    getDownloadData: getDownloadDataCreator,
    isAvailable
  } = downloadItemsActionControlFacet.evaluate(controlHandler)(itemCache, { // tslint:disable-line:max-line-length
    itemKeys: itemKeys,
    downloader: downloader,
    isAvailable: false
  });

  if (!Downloader || !getDownloadDataCreator || !isAvailable) {
    return {
      action: null
    };
  }

  const { dispatch } = itemCache;

  const getDownloadData = () => dispatch(getDownloadDataCreator());

  return {
    action: (
      <HeadlessAction
        isAvailable={ isAvailable }
        { ...getItemActionEngagement(itemCache, {
          itemKey,
          engagement: {
            name: 'Download',
            isIntentional: true
          }
        }) }
      >
        { (wizardProps: IWizardActionStateProps) => {
          return (
            // In case the background action needs the current itemsScopeItemKey,
            // wrap the downloader with an ItemsScope pointed at the same state.
            <ItemsScope itemsScopeItemKey={ itemsScopeItemKey }>
              <Downloader
                itemKeys={ itemKeys }
                getDownloadData={ getDownloadData }
                { ...wizardProps }
              />
            </ItemsScope>
          );
        } }
      </HeadlessAction>
    )
  };
})(ActionSelector);

export type IDownloadActionProps = IDecorateWithSelectedItemsProps<IDecorateWithCurrentItemAsFallbackProps<IItemDownloadActionProps>>;

export const DownloadAction: React.ComponentType<IDownloadActionProps> =
  withSelectedItems()(withCurrentItemAsFallback()(ItemDownloadAction));
